import React from "react";
import PropTypes from "prop-types";
import UniversalLink from "@plone/volto/components/manage/UniversalLink/UniversalLink";
import cx from "classnames";
import config from "@plone/volto/registry";
import { flattenToAppURL } from "@plone/volto/helpers/Url/Url";

/**
 * Determine which image field and source strategy is available.
 *
 * Priority:
 * 1. properties.image                — News Item lead image (embedded)
 * 2. properties.preview_image        — Preview image behavior (embedded)
 * 3. properties.preview_image_link   — Linked preview image object (relation field)
 * 4. image_scales.preview_image_link — Linked preview image via image_scales
 * 5. image_scales.image              — Embedded image via image_scales
 *
 * Returns one of:
 *   { strategy: 'field', imageField: string }
 *   { strategy: 'linked', basePath: string }
 *   null
 */
const getImageStrategy = (properties) => {
  if (properties.image) {
    return { strategy: "field", imageField: "image" };
  }
  if (properties.preview_image) {
    return { strategy: "field", imageField: "preview_image" };
  }
  // Linked preview image stored as a relation object directly on properties
  if (properties.preview_image_link?.["@id"]) {
    const basePath = flattenToAppURL(properties.preview_image_link["@id"]);
    return { strategy: "linked", basePath };
  }
  if (properties.image_scales?.preview_image_link?.length > 0) {
    const basePath = properties.image_scales.preview_image_link[0]?.base_path;
    if (basePath) {
      return { strategy: "linked", basePath };
    }
  }
  if (properties.image_scales?.image?.length > 0) {
    const basePath = properties.image_scales.image[0]?.base_path;
    if (basePath) {
      return { strategy: "linked", basePath };
    }
  }
  return null;
};

/**
 * Build a srcSet string from a linked image basePath.
 * Mirrors the scale names used in CommonItemRenderer.
 */
const buildLinkedSrcSet = (basePath) => {
  const scales = [
    ["mini", 200],
    ["preview", 400],
    ["teaser", 600],
    ["large", 800],
    ["larger", 1000],
    ["great", 1200],
    ["huge", 1600],
  ];
  return scales
    .map(([scale, w]) => `${basePath}/@@images/image/${scale} ${w}w`)
    .join(", ");
};

const View = ({ data, properties }) => {
  const Image = config.getComponent({ name: "Image" }).component;
  const imageStrategy = getImageStrategy(properties);

  const wrapWithLink = (content) => {
    if (data.href) {
      return (
        <UniversalLink
          href={data.href}
          openLinkInNewTab={data.openLinkInNewTab}
        >
          {content}
        </UniversalLink>
      );
    }
    return content;
  };

  const renderImage = () => {
    if (!imageStrategy) return null;

    const sharedClassName = cx({ "full-width": data.align === "full" });
    const altText = properties.image_caption || "";

    if (imageStrategy.strategy === "field") {
      return (
        <Image
          className={sharedClassName}
          item={properties}
          imageField={imageStrategy.imageField}
          sizes={config.blocks.blocksConfig.leadimage.getSizes(data)}
          alt={altText}
          responsive={true}
        />
      );
    }

    // strategy === 'linked': plain <img> with srcSet constructed from basePath
    const { basePath } = imageStrategy;
    const src = `${basePath}/@@images/image/preview`;
    const srcSet = buildLinkedSrcSet(basePath);
    const sizes =
      config.blocks.blocksConfig.leadimage.getSizes(data) ??
      "(min-width: 768px) 50vw, 100vw";

    return (
      <img
        className={cx("ui image", sharedClassName)}
        src={src}
        srcSet={srcSet}
        sizes={sizes}
        alt={altText}
        loading="lazy"
        width="100%"
        height="auto"
        // style={{ aspectRatio: "16/9" }}
      />
    );
  };

  const image = renderImage();

  return (
    <p
      className={cx(
        "block image align",
        { center: !Boolean(data.align) },
        data.align,
      )}
    >
      {image && wrapWithLink(image)}
    </p>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
View.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  properties: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default View;
